import { LitElement, html } from "lit-element";
import { icon_zoomIn, icon_zoomOut, undo, redo, deleteIcon } from "./Resources/drawing-icons.js";
import { close, download, upload } from "../../styles/icons.js";
import { SharedStyles } from "../../styles/shared-styles.js";

export class InspxDrawingTopToolbar extends LitElement {
	render() {
		return html`
        ${SharedStyles}
        <style>
            :host {
                height: 60px;
            }

            .toolbar {
                color: #fff;
                fill: #fff;
                padding-right: 10px;
                align-items: center;
                height: 60px;
                background-color: var(--primary-color);
                display: flex;
                flex-direction: row;
                justify-content: space-between;
            }

            .toolbar > div {
              	display: flex;
				align-items: center;
            }
            
            .icon,
            .devider {
                margin: 0 10px;
            }

            .devider:after {
                content: "|";
            }
            
            .icon {
                cursor: pointer;
            }

            .icon[disabled] {
                color: #757575;
                fill: #4e4e58;
                cursor: not-allowed;   
            }

            .download, .upload{
                margin-top: 10px;
            }

            input {
                display: flex;
                align-items: center;
            }

			.icon > svg {
				margin-top: 5px;
            }

			:host([is-signature]) .toolbar > div:not(:first-child),
			.toolbar > div.signature-label {
				display: none;
			}
			 
			:host([is-signature]) .toolbar > div.signature-label {
				display: block;
				text-align: center;
    			flex-grow: 1;
			}
        </style>

        <div class="toolbar">
            <div>
                <div class="icon" @click="${this.onClose}">${close}</div>
            </div>
			<div class="signature-label">Draw a signature</div>
            <div>
                <div class="icon upload" @click="${this.onUpload}">${upload}</div>
                <div class="icon download" @click="${this.onDownload}">${download}</div>
                <div class="devider"></div>
				${this.legendToggle} 
				${this.gridToggle}
                <div class="devider"></div>
                <div class="icon" @click="${e => this.zoomIn(e)}" ?disabled="${this._isZoomInDisabled}">${icon_zoomIn}</div>
                <div class="icon" @click="${e => this.zoomOut(e)}" ?disabled="${this._isZoomOutDisabled}">${icon_zoomOut}</div>
                <div class="devider"></div>
                <div class="icon" @click="${e => this.undo(e)}" ?disabled="${this._isUndoDisabled}">${undo}</div>
                <div class="icon" @click="${e => this.redo(e)}" ?disabled="${this._isRedoDisabled}">${redo}</div>
                <div class="devider"></div>
                <div class="icon" @click="${e => this.delete(e)}" ?disabled="${this._isDeleteDisabled}">${deleteIcon}</div>
            </div>
        </div>`;
	}

	delete() {
		const o = this.canvas.getActiveObject();
		if (o && !(o._objects instanceof Array)) {
			this.canvas.remove(o);
		} 

		const g = this.canvas.getActiveObjects();
		if (g && g.length > 0) {
			this.history.pause();
			g.forEach((o) => { this.canvas.remove(o); });
			this.canvas.renderAll();
			this.history.unpause();
			this.history.save();
		}
	}

	_formatZoom(zoom) {
		return (zoom || 1).toFixed(2);
	}

	zoomIn() {
		if (!this._isZoomInDisabled) {
			this.zoomModule.zoomIn();
		}
	}

	zoomOut() {
		if (!this._isZoomOutDisabled) {
			this.zoomModule.zoomOut();
		}
	}

	undo() {
		if (!this.history.isStart) {
			this.history.undo();
		}
	}

	redo() {
		if (!this.history.isEnd) {
			this.history.redo();
		}
	}

	updated() {
		if (!this._subscribed && this.canvas) {
			this._subscribed = true;
			this._selectionCreatedHandler = () => { this._isDeleteDisabled = false; };
			this._selectionClearedHandler = () => { this._isDeleteDisabled = true; };
			this.canvas.on("selection:created", this._selectionCreatedHandler);
			this.canvas.on("selection:cleared", this._selectionClearedHandler);
		}
	}

	disconnectedCallback() {
		if (this._subscribed) {
			this.canvas.off("selection:created", this._selectionCreatedHandler);
			this.canvas.off("selection:cleared", this._selectionClearedHandler);
		}
	}

	constructor(canvas, history, zoom, closeFunc, legend, grid, downloadFunc, uploadFunc) {
		super();
		this.onClose = closeFunc;
		this.onDownload = downloadFunc;
		this.onUpload = uploadFunc;
		this.canvas = canvas;
		this.history = history;
		this.history.saveCallback = () => {
			this._isUndoDisabled = this.history.isStart;
			this._isRedoDisabled = this.history.isEnd;
		};
		this._isZoomInDisabled = false; 
		this._isZoomOutDisabled = false;
		this.zoomModule = zoom;
		this.zoomModule.zoomChangeCallback = () => {
			this._isZoomInDisabled = this.zoomModule.isMaxZoomReached;
			this._isZoomOutDisabled = this.zoomModule.isMinZoomReached;
		};
		this._isUndoDisabled = true;
		this._isRedoDisabled = true;
		this._isDeleteDisabled = true;
		this.legendToggle = legend;
		this.gridToggle = grid;
	}

	static get properties() {
		return {
			_isUndoDisabled: { type: Boolean },
			_isRedoDisabled: { type: Boolean },
			_isZoomInDisabled: { type: Boolean },
			_isZoomOutDisabled: { type: Boolean },
			_isDeleteDisabled: { type: Boolean },
			canvas: { type: Object },
		};
	}
}

window.customElements.define(
	"inspx-drawing-top-toolbar",
	InspxDrawingTopToolbar
);
